/**
 * The **"Press F to auto-fire your bow!" prompt** (Fire your bow!, ADR-0255;
 * Auto-fire the duel bow): a small bottom-centre banner that appears while the local
 * player is in a live duel and their opponent has kited out of punching range
 * ({@link ../sim/duel.DUEL_SWING_RANGE}) but is still within bow range
 * ({@link ../sim/duel.DUEL_BOW_RANGE}). It tells the player they can no longer punch
 * the runner but *can* shoot them - closing the old run-away-for-a-draw exploit
 * legibly. F is a *toggle*: one press starts continuous auto-fire at the bow's legal
 * cadence (the ranged twin of the auto-swing), a second press stops it.
 *
 * Presentation-only, an instance of the shared banner primitive
 * (`src/ui/prompt.ts` `createBanner`, Chassis Slice H): this module owns a single
 * DOM element appended to `document.body` (no `index.html` markup), holds no duel
 * state, and sends nothing on the wire - the duel installer decides when to
 * `show()`/`hide()` it from the replicated combat stance + the opponent id + the
 * live tile distance, and the `fire-bow` keybinding sends the actual `FireBow`
 * command. The banner is `pointer-events: none` (the primitive's contract) so it
 * never swallows a click beneath it.
 */
import { createBanner } from './ui/prompt';

/** The handle the duel installer drives the prompt through. */
export interface DuelBowPromptHandle {
  /** Reveal the banner (idempotent). */
  show(): void;
  /** Hide the banner (idempotent). */
  hide(): void;
  /** Whether the banner is currently visible. */
  isVisible(): boolean;
}

/** The banner copy - the one source of the prompt string (pinned by the test). */
export const BOW_PROMPT_TEXT = 'Press F to auto-fire your bow!';

/**
 * Build the `#duel-bow-prompt` banner and return its handle. Hidden until `show()`.
 * `doc` is injectable for tests; live callers default to `document`.
 */
export function initDuelBowPrompt(doc: Document = document): DuelBowPromptHandle {
  return createBanner(doc, {
    id: 'duel-bow-prompt',
    text: BOW_PROMPT_TEXT,
    bottom: '96px',
    // The war-toned amber the duel already uses for combat feedback, so the prompt
    // reads as "you are in a fight" chrome rather than a neutral notice.
    color: '#e7b86a',
  });
}
